/**
 * Página MOBILE "6DU" (Task #25b + #15 joint — tracker esperado × recebido).
 * Tela dedicada do glance pré-09h: o que cai no próximo 6º dia útil (CCEE).
 *
 * FONTE ÚNICA da tela: GET /api/recebimentos-6du/tracker — o ESPERADO é o
 * mesmo pipeline do card 6DU (#15/#16, âncora única data_6du, doutrina #207)
 * e o RECEBIDO (baixa ao vivo) vem do Sync F0 via join por (id_contrato,
 * competência) + fallback cliente+valor (~9,5% sem contrato).
 *
 * RECEBIDO (2ª metade, liga o "em breve" do #25b): barra esperado × recebido
 * 2-tons — conferido SÓLIDO (verde) + caiu-a-conferir HACHURADO (âmbar; o
 * registrado-Omie entra no hachurado pra barra somar o recebido_total e ganha
 * linha PRÓPRIA na legenda quando > 0). As-of honesto "Extrato HH:MM (D+1)"
 * do extrato_atualizado_em (parseUtcNaive — fix de fuso F-25a-1); D+1 até o
 * F2 intraday do Sync (flag_d1).
 *
 * DEGRADAÇÃO HONESTA: recebido_disponivel=false (Sync down/403) → o card
 * mostra "recebido indisponível" EXPLÍCITO (não volta pro "em breve", não
 * finge barra) e o esperado segue vivo (dado nosso). Anti-mock: esperado
 * vazio → mensagem explícita; loading "…" ≠ erro (ErroNote) ≠ sem dado "—".
 *
 * PAGARAM × FALTAM (o QUEM, não só o placar — mock aprovado
 * docs/mocks/mobile_6du_pagaram_faltam_mock.html): a lista única virou 2
 * seções por CONTRATO — "✓ Pagaram · N" (valor_recebido desc, pill de
 * status + "caiu DD/MM") e "Faltam · M" (valor_esperado desc, os grandes a
 * cobrar no topo; chip ICMS #16 preservado). COMPLETA, sem corte (#25b-1).
 * Sync down → SEM bloco Pagaram (nunca finge pago), Faltam = todos;
 * 0 pagaram cedo → "ninguém ainda" honesto.
 */
import type { CSSProperties } from "react";
import { useMemo } from "react";
import { getRecebimentos6duTracker } from "../../api/client";
import type { StatusRecebido6du, Tracker6duLinha, Tracker6duResponse } from "../../api/types";
import { useLastSync } from "../../hooks/useLastSync";
import { formatarDataLongaBr, parseUtcNaive } from "../../utils/dateBr";
import { fmtBRL } from "../../utils/formatters";
import { zeusAsOf } from "./MobilePnl";
import { Card, ErroNote, MONEY, MONEY_ABBR, MobileHeader } from "./mobileUi";
import { useFetchLocal } from "./useFetchLocal";

/** Rótulo + pill visual dos status (contrato do tracker; pills do mock). */
export const STATUS_UI: Record<StatusRecebido6du, { rotulo: string; pill: string }> = {
  conferido: {
    rotulo: "✓ conferido",
    pill: "bg-emerald-400/15 text-indra-positivo",
  },
  caiu_a_conferir: {
    rotulo: "● a conferir",
    pill: "bg-amber-400/20 text-amber-300",
  },
  registrado_omie: {
    rotulo: "reg. Omie",
    pill: "bg-sky-400/15 text-sky-300",
  },
  aguardando: { rotulo: "aguardando", pill: "bg-white/10 text-white/40" },
};

/** "caiu DD/MM" da data_recebimento (YYYY-MM-DD); null = omite. */
export function caiuEm(dataRecebimento: string | null): string | null {
  if (!dataRecebimento || dataRecebimento.length < 10) return null;
  return `caiu ${dataRecebimento.slice(8, 10)}/${dataRecebimento.slice(5, 7)}`;
}

/** Split por contrato (mock Pagaram × Faltam): PAGOU = recebido > 0 OU status
 * de progresso (≠ aguardando/null). recebido indisponível → NADA em pagaram
 * (nunca finge pago). */
export function splitPagaramFaltam(t: Tracker6duResponse | null): {
  pagaram: Tracker6duLinha[]; faltam: Tracker6duLinha[];
} {
  if (!t) return { pagaram: [], faltam: [] };
  if (!t.recebido_disponivel) return { pagaram: [], faltam: [...t.linhas] };
  const pagou = (l: Tracker6duLinha) =>
    (l.valor_recebido ?? 0) > 0 || (l.status !== null && l.status !== "aguardando");
  return {
    pagaram: t.linhas.filter(pagou)
      .sort((a, b) => (b.valor_recebido ?? 0) - (a.valor_recebido ?? 0)),
    faltam: t.linhas.filter((l) => !pagou(l))
      .sort((a, b) => b.valor_esperado - a.valor_esperado),
  };
}

/** Hachura âmbar do segmento "caiu a conferir" (2º tom da barra). */
const HACHURA_AMBAR: CSSProperties = {
  backgroundImage:
    "repeating-linear-gradient(45deg, rgba(240,180,41,.85) 0 4px, rgba(240,180,41,.35) 4px 8px)",
};

/** "Extrato HH:MM (D+1)" do timestamp UTC do Sync; null = omite. */
export function extratoAsOf(iso: string | null, flagD1: boolean): string | null {
  if (!iso) return null;
  const d = parseUtcNaive(iso);
  if (Number.isNaN(d.getTime())) return null;
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `Extrato ${hh}:${mm}${flagD1 ? " (D+1)" : ""}`;
}

export function Mobile6du({ onLogout, onRefresh }: {
  onLogout: () => void;
  onRefresh?: () => void;
}) {
  const tracker = useFetchLocal<Tracker6duResponse>(() => getRecebimentos6duTracker());
  const lastSync = useLastSync();

  const t = tracker.data;
  const asOf = zeusAsOf(
    lastSync.data ? lastSync.data.by_source[lastSync.data.primary]?.finished_at : null,
  );
  const data6du = t?.data_6du ?? null;
  const temEsperado = t !== null && t.linhas.length > 0;

  const recebidoOk = t?.recebido_disponivel === true;
  const esperadoTotal = t?.total_esperado ?? 0;
  const conferido = t?.recebido_conferido ?? 0;
  // barra 2-tons: hachurado = caiu-a-conferir + registrado-Omie (a barra soma
  // o recebido_total; o Omie ganha linha própria na legenda quando > 0).
  const aConferir = (t?.recebido_a_conferir ?? 0) + (t?.recebido_registrado_omie ?? 0);
  const recebidoTotal = t?.recebido_total ?? 0;
  const pct = esperadoTotal > 0 ? Math.round((recebidoTotal / esperadoTotal) * 100) : 0;
  const wConferido = esperadoTotal > 0 ? Math.min(100, (conferido / esperadoTotal) * 100) : 0;
  const wAConferir = esperadoTotal > 0
    ? Math.min(100 - wConferido, (aConferir / esperadoTotal) * 100)
    : 0;
  const extrato = t ? extratoAsOf(t.extrato_atualizado_em, t.flag_d1) : null;

  // Pagaram × Faltam (contagem por CONTRATO, coerente com o tracker).
  const { pagaram, faltam } = useMemo(() => splitPagaramFaltam(t), [t]);
  const totalPagaram = pagaram.reduce((s, l) => s + (l.valor_recebido ?? 0), 0);
  const totalFaltam = faltam.reduce((s, l) => s + l.valor_esperado, 0);

  return (
    <div className="min-h-screen bg-indra-azul" data-testid="mobile-6du">
      <MobileHeader subtitle="Próximo 6DU" onLogout={onLogout} onRefresh={onRefresh} />
      <main className="px-4 py-4 flex flex-col gap-4 max-w-md mx-auto">
        {asOf && (
          <p className="text-[11px] text-indra-turquesa/80 -mb-2" data-testid="mobile-6du-asof">
            {asOf}
          </p>
        )}

        {/* 1 · ESPERADO — número grande do glance (Σ do endpoint, não re-soma). */}
        <Card title="Esperado · Liquidação CCEE" testId="mobile-6du-card-esperado">
          {/* data_6du é YYYY-MM-DD → meia-noite LOCAL ("T00:00:00", padrão do
              useProximo6du) — new Date(ymd) cru seria UTC e voltaria 1 dia no BRT. */}
          <div className="text-xl font-black text-white mb-1" data-testid="mobile-6du-data">
            {data6du ? formatarDataLongaBr(new Date(data6du + "T00:00:00")) : tracker.loading ? "…" : "—"}
          </div>
          {tracker.erro && <ErroNote testId="mobile-6du-esperado-erro" />}
          <div
            className="text-3xl font-black font-sans text-indra-positivo whitespace-nowrap tabular-nums"
            data-testid="mobile-6du-esperado-total"
          >
            {t ? fmtBRL(t.total_esperado, MONEY_ABBR) : tracker.loading ? "…" : "—"}
          </div>
          <div className="text-xs text-white/60 mt-1" data-testid="mobile-6du-esperado-sub">
            {t ? `${t.n_contrapartes} contrapartes · ${t.n_trades} trades` : "—"}
          </div>
        </Card>

        {/* 2 · RECEBIDO — barra esperado × recebido (#15 joint, Sync F0). */}
        <Card title="Recebido · baixa ao vivo" testId="mobile-6du-card-recebido">
          {!t ? (
            <div className="text-white/60 text-sm">{tracker.loading ? "…" : "—"}</div>
          ) : !recebidoOk ? (
            /* Degradação honesta: Sync down ≠ "em breve" ≠ zero — indisponível. */
            <div data-testid="mobile-6du-recebido-indisponivel">
              <div className="text-lg font-semibold text-white/40">recebido indisponível</div>
              <div className="text-xs text-white/60 mt-1">
                Sem resposta do Sync agora — o esperado abaixo segue vivo.
                Toque em atualizar (↻) pra tentar de novo.
              </div>
            </div>
          ) : (
            <div data-testid="mobile-6du-recebido-vivo">
              <div className="text-xl font-black text-white tabular-nums" data-testid="mobile-6du-recebido-headline">
                {fmtBRL(recebidoTotal, MONEY_ABBR)}{" "}
                <span className="text-sm font-normal text-white/60">
                  de {fmtBRL(esperadoTotal, MONEY_ABBR)} esperado · {pct}%
                </span>
              </div>
              {/* barra 2-tons: conferido sólido + a-conferir hachurado */}
              <div
                className="mt-2 h-3 w-full rounded-full bg-white/10 overflow-hidden flex"
                role="progressbar"
                aria-valuenow={pct}
                aria-valuemin={0}
                aria-valuemax={100}
                data-testid="mobile-6du-recebido-barra"
              >
                <div
                  className="h-full bg-indra-positivo"
                  style={{ width: `${wConferido}%` }}
                  data-testid="mobile-6du-barra-conferido"
                />
                <div
                  className="h-full"
                  style={{ width: `${wAConferir}%`, ...HACHURA_AMBAR }}
                  data-testid="mobile-6du-barra-aconferir"
                />
              </div>
              <div className="mt-2 flex flex-col gap-0.5 text-xs" data-testid="mobile-6du-recebido-legenda">
                <div className="flex justify-between">
                  <span className="text-indra-positivo">Conferido</span>
                  <span className="font-mono tabular-nums text-slate-100">
                    {fmtBRL(conferido, MONEY)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-amber-300">Caiu a conferir</span>
                  <span className="font-mono tabular-nums text-slate-100">
                    {fmtBRL(t.recebido_a_conferir ?? 0, MONEY)}
                  </span>
                </div>
                {(t.recebido_registrado_omie ?? 0) > 0 && (
                  <div className="flex justify-between" data-testid="mobile-6du-legenda-omie">
                    <span className="text-sky-300">Registrado Omie</span>
                    <span className="font-mono tabular-nums text-slate-100">
                      {fmtBRL(t.recebido_registrado_omie ?? 0, MONEY)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-white/60">Aguardando</span>
                  <span className="font-mono tabular-nums text-slate-100">
                    {fmtBRL(t.aguardando ?? 0, MONEY)}
                  </span>
                </div>
              </div>
              {extrato && (
                <div className="text-[11px] text-white/50 mt-2" data-testid="mobile-6du-extrato-asof">
                  {extrato}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* 3 · ✓ PAGARAM — quem já caiu (valor_recebido desc). Sync down →
            card NEM monta (nunca finge pago). */}
        {t && recebidoOk && temEsperado && (
          <Card title={`✓ Pagaram · ${pagaram.length}`} testId="mobile-6du-card-pagaram">
            {pagaram.length === 0 ? (
              <div className="text-white/60 text-sm" data-testid="mobile-6du-pagaram-vazio">
                ninguém ainda — o primeiro que cair aparece aqui.
              </div>
            ) : (
              <>
                <div className="text-xs text-white/60 -mt-1 mb-1 text-right font-mono tabular-nums"
                     data-testid="mobile-6du-pagaram-total">
                  {fmtBRL(totalPagaram, MONEY)}
                </div>
                {pagaram.map((l) => (
                  <div
                    key={`${l.contraparte}-${l.id_contrato ?? "s/c"}`}
                    className="flex items-center gap-2 py-2 border-b border-white/5 last:border-0"
                    data-testid="mobile-6du-pagaram-linha"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-slate-200 truncate">{l.contraparte}</div>
                      <div className="text-[11px] text-white/50 mt-0.5">
                        {l.id_contrato !== null ? `contrato ${l.id_contrato}` : "sem nº de contrato"}
                        {caiuEm(l.data_recebimento) ? ` · ${caiuEm(l.data_recebimento)}` : ""}
                      </div>
                    </div>
                    {l.status && (
                      <span
                        className={`text-[10px] font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${STATUS_UI[l.status].pill}`}
                        data-testid={`mobile-6du-status-${l.status}`}
                      >
                        {STATUS_UI[l.status].rotulo}
                      </span>
                    )}
                    <div className="text-sm font-mono tabular-nums text-slate-100 whitespace-nowrap flex-shrink-0">
                      {fmtBRL(l.valor_recebido ?? 0, MONEY)}
                    </div>
                  </div>
                ))}
              </>
            )}
          </Card>
        )}

        {/* 4 · FALTAM — quem ainda não pagou (valor_esperado desc, os grandes
            a cobrar no topo). COMPLETA, sem corte (#25b-1); chip ICMS (#16). */}
        <Card
          title={`Faltam · ${t && temEsperado ? faltam.length : "—"}`}
          testId="mobile-6du-card-faltam"
        >
          {tracker.erro ? (
            <ErroNote testId="mobile-6du-contratos-erro" />
          ) : !t ? (
            <div className="text-white/60 text-sm">{tracker.loading ? "…" : "—"}</div>
          ) : !temEsperado ? (
            <div className="text-white/60 text-sm" data-testid="mobile-6du-contratos-vazio">
              Sem recebimentos esperados no próximo 6DU.
            </div>
          ) : faltam.length === 0 ? (
            <div className="text-indra-positivo text-sm" data-testid="mobile-6du-faltam-vazio">
              Todos pagaram. ✓
            </div>
          ) : (
            <>
              <div className="text-xs text-white/60 -mt-1 mb-1 text-right font-mono tabular-nums"
                   data-testid="mobile-6du-faltam-total">
                {fmtBRL(totalFaltam, MONEY)}
              </div>
              {faltam.map((l) => (
                <div
                  key={`${l.contraparte}-${l.id_contrato ?? "s/c"}`}
                  className="flex items-center justify-between gap-3 py-2 border-b border-white/5 last:border-0"
                  data-testid="mobile-6du-faltam-linha"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-slate-200 truncate">{l.contraparte}</div>
                    <div className="flex items-center gap-1.5 text-[11px] text-white/50 mt-0.5">
                      {l.id_contrato !== null ? `contrato ${l.id_contrato}` : "sem nº de contrato"}
                      {l.tem_icms && (
                        <span
                          className="px-1.5 py-0.5 rounded bg-amber-400/15 text-amber-300 font-semibold"
                          data-testid="mobile-6du-chip-icms"
                        >
                          ICMS
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="text-sm font-mono tabular-nums text-slate-100 whitespace-nowrap flex-shrink-0">
                    {fmtBRL(l.valor_esperado, MONEY)}
                  </div>
                </div>
              ))}
            </>
          )}
        </Card>

        <p className="text-center text-white/40 text-xs py-2">
          Detalhe completo no desktop (Fluxo · card 6DU).
        </p>
      </main>
    </div>
  );
}
